import { Fragment, memo, useEffect, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ChevronLeft, ChevronRight, Loader2, Minus, RefreshCw, RotateCcw, ShoppingCart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useReactTable,
  type Row,
  getCoreRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
  type ExpandedState,
  type PaginationState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { useMaterialRequestStore, usePendingQuantityStore } from '../store';
import { createExpandableColumns, PicklistSelectionProvider } from '../components/expandable-picklist-columns';
import { MaterialRequestDetailSheet } from '../components/MaterialRequestDetailSheet';
import { type PicklistCreationTableRow, type MrsTableRow, type ItemTableRow, type ItemSelectionState, type SelectedItemDisplay, type CreatePicklistItemRequest, type PicklistItemDto } from '../types';
import { BinAllocationDialog } from './BinAllocationDialog';
import { heldQuantityByMrsItem, claimedQuantityByItem, deductHoldings } from './picklistHoldings';
import { CartItemCard } from './CartItemCard';
import { CreationReviewDialog } from './CreationReviewDialog';
import { buildItemPlans, planKey, splitBinsForPayload, type ItemPlan, type SavedAllocation, outOfStockNotice } from './itemPlan';
import { creationCoverage } from '../utils/creationCoverage';
import { freeStockStatus } from '../utils/freeStock';
import { PicklistBreadcrumb } from './PicklistHeaderBits';
import { useCrumbYield, useDockFits, useWorkspaceHost } from './useDockFits';
import { useStockStore } from '@features/inventory';
import { DebouncedSearchInput } from '@/components/ui/debounced-search-input';
import { TableSkeleton } from '@/components/ui/skeleton';
import { materialRequestService } from '../services';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * A row is selectable for a picklist only if it resolves to a real, stockable NetSuite item.
 * Non-catalog (Service / "DUMMY ITEM CODE") and unlinked (netsuiteItemId 0) lines are excluded
 * from selection, stock fetch, and the cart (86d3gmemk).
 */
const isSelectableItem = (item: ItemTableRow): boolean =>
  !item.isNonCatalog && item.netsuiteItemId !== 0;

/**
 * Width budget for the selection table, in px, summing to exactly the 1024px min-width.
 *
 * ## Why this is declared rather than left to the browser
 *
 * Auto table layout sizes a column by the widest content in ANY row, and item rows put a
 * 150-character NetSuite spec string into a cell spanning `identifier` + `description`. Measured
 * before this budget existed: those two columns took 349px and 391px — 740px, 48% of the table —
 * to display "MRS-100029188" and a person's name, while Date Needed was starved to 99px and wrapped
 * "Sep 15, 2026" onto two lines. The two blank columns took another 294px to render nothing on MRS
 * rows. The row was spacious and still wrapping, which is the signature of content-driven layout.
 *
 * So the table is `table-fixed` and these widths are the source of truth. Anything wider than the
 * sum is distributed proportionally, so every column grows on a big screen and nothing is starved
 * on a small one.
 *
 * ## How the budget is apportioned
 *
 * `identifier` + `description` is the widest claim because on ITEM rows those two carry the merged
 * identity cell, and the item name is the densest thing on this screen — it is what a picker reads
 * to tell one plate from another. It is clamped to two lines, so every pixel it does not get is a
 * spec string ending in an ellipsis.
 *
 * The space comes from the columns that are EMPTY on item rows (`approvedBy`, `itemCount`,
 * `actions`) and from ones measured to be carrying far more room than their content: the approver
 * name needs ~166px and had 217px, the icon button needs 28px and had 61px. Nothing here is a round
 * number for its own sake — each was measured against real data.
 *
 * Each figure is content + 16px (`px-2` on both sides of a cell).
 *
 * ## 2026-10-05 — retuned so it stops scrolling too soon (owner)
 *
 * The table scrolled sideways at EVERY width, not only narrow ones. `actions` was budgeted 34px for "a 28px icon
 * button", but the button was `size="sm"` (40px wide) in a `px-2` cell: 56px of content in a 34px column. In a fixed
 * table that overflow does not widen the column, it spills past the table's right edge, and the scroller grew a
 * scrollbar for those 20-odd pixels however much room it had. The button is now a true 28px icon button and the
 * column is 28 + 16.
 *
 * The same pass takes the minimum from 1078 to 1024, out of the three places measured to have slack:
 * `deptEndUse` (blank on MRS rows), and `identifier` / `description`, which only ever show an MRS number and a name.
 * On an item row the identity cell spans those plus `dateOrQty` — 490px at the minimum, was 542 — and every pixel
 * above the minimum is still shared out proportionally.
 */
const COLUMN_WIDTHS: Record<string, number> = {
  select: 36, //      17px checkbox, indented on item rows
  expand: 30, //      24px chevron button
  identifier: 150, // "MRS-100029188" at mono 13px (~110px), plus the external-link glyph
  description: 176, // requestor on MRS rows ("MENDOZA, Zaimoun Rey R" ~145px + padding)
  deptEndUse: 64, //   ⚠️ EMPTY on MRS rows and swallowed by the identity span on item rows — this
  //                   column renders nothing anywhere, so it is the cheapest place to take width from
  dateOrQty: 100, //   "Sep 15, 2026" is ~80px at 12.5px — the column that used to wrap
  stockStatus: 168, // ⚠️ on ITEM rows this slot carries DEPT / END USE — 168 fits "WATER TREATMENT
  //                   PLANT" on ONE line at 12.5px, which is what stops the four-line stack
  approvedBy: 172, //  fits "ALCE, Jhon Leonard G." on one line; the two longest names still take two
  itemCount: 84, //    the "6 item(s)" pill, which must not wrap
  actions: 44, //      28px icon button + the cell's 16px. Anything narrower spills past the table's edge
};

/**
 * ⚠️ An item row's cells do NOT line up with the header above them, and the widths above are tuned
 * for where the CONTENT lands rather than for the column name.
 *
 * The identity cell spans 4 (identifier + description + deptEndUse + dateOrQty), so everything after
 * it shifts two columns left:
 *
 * | item row shows | actually sits in    | width |
 * |----------------|---------------------|-------|
 * | Dept / End Use | `stockStatus`       | 168   |
 * | Requested      | `approvedBy`        | 172   |
 * | Stock          | `itemCount+actions` | 128   |
 *
 * So widening Dept / End Use on an item row means widening `stockStatus`, which is empty on MRS
 * rows and costs nothing there. Read the table twice before retuning a width (owner, 2026-09-18).

/**
 * What each column means on an ITEM row, which is not what the top header says it means — an item
 * row's `dateOrQty` holds a quantity, not a date.
 *
 * One map, two readers: the group sub-header renders from it, and the cell tooltips answer from it.
 * Keeping them on the same source is the point — a tooltip that disagreed with the label directly
 * above it would be worse than no tooltip at all.
 */
const ITEM_COLUMN_LABELS: Record<string, string> = {
  identifier: 'Item',
  deptEndUse: 'Dept / End Use',
  dateOrQty: 'Requested',
  stockStatus: 'Stock',
};

/**
 * Item-row cells that answer a hover with their column name.
 *
 * `identifier` and `stockStatus` are absent on purpose: both already carry a RICHER tooltip — the
 * full item spec, and the stock breakdown — and replacing "4LA-SUP-LHLFHD-S · SUPPLY AND
 * APPLICATION…" with the word "Item" would be a downgrade. Nesting a second tooltip inside them
 * would fight the first.
 */
const ITEM_COLUMNS_WITH_LABEL_TOOLTIP = new Set(['deptEndUse', 'dateOrQty']);

/**
 * One compact label row per expanded MRS group.
 *
 * The top header row can only honestly label MRS-level columns — this table is one grid with two
 * row types, and a single header that mixes both reads as if every column applies to every row.
 * So the item columns are labelled here, next to the rows they describe.
 *
 * Deliberately NOT a synthetic row in the TanStack row model. `subRows` is typed `ItemTableRow[]`,
 * so a third row type would ripple through tri-state selection, the search filter and the cart —
 * all behaviour this change is not allowed to touch. The only argument for putting it in the model
 * was keeping pagination honest, and `paginateExpandedRows: false` already does that.
 */
function ItemGroupSubHeader() {
  const cell =
    'h-6 py-0 text-[9.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground';

  return (
    // Carries the item rows' left accent so the header and its items read as one block.
    <TableRow className="bg-muted/60 hover:bg-muted/60 !border-l-4 !border-l-primary/30">
      <TableCell className={cell} />
      <TableCell className={cell} />
      <TableCell className={`${cell} pl-3.5`} colSpan={4}>
        {ITEM_COLUMN_LABELS.identifier}
      </TableCell>
      <TableCell className={cell}>{ITEM_COLUMN_LABELS.deptEndUse}</TableCell>
      <TableCell className={cell}>{ITEM_COLUMN_LABELS.dateOrQty}</TableCell>
      <TableCell className={cell} colSpan={2}>
        {ITEM_COLUMN_LABELS.stockStatus}
      </TableCell>
    </TableRow>
  );
}

export interface ItemSelectionPanelProps {
  /** The trailing breadcrumb crumb — "Create Picklist", "Add items". */
  title: string;
  /** Where the leading "Picklists" crumb goes. The LIST, not a picklist. */
  backPath: string;
  /** Add-items only: renders a middle crumb linking back to that picklist. */
  picklistNumber?: string;
  picklistPath?: string;
  submitLabel: string;
  submittingLabel: string;
  confirmTitle: string;
  confirmDescription: string;
  confirmButtonLabel: string;
  /**
   * ADD ITEMS only: the lines the target picklist already carries. Undefined on the create route.
   *
   * 🕳️ This used to be `excludeMaterialRequestItemIds` and the table simply DROPPED every line it named
   * — which meant an MRS line with quantity still unallocated became invisible the moment any of it
   * reached the picklist, with nothing on screen to say why or what to do about it (owner report
   * 2026-09-21). The exclusion was also redundant: `GetWithAvailableItemsAsync` already drops any line
   * whose `availableQuantity` has reached 0, so a line that is FULLY covered never arrives here at all.
   * A line that survives the server's filter has quantity left, and is now shown and topped up.
   *
   * It is kept (rather than deleted outright) for two jobs the exclusion could not do: annotating the
   * row with what the picklist already holds, and keeping the allocation dialog from re-offering units
   * this picklist has already claimed.
   */
  existingPicklistItems?: PicklistItemDto[];
  /**
   * ADD ITEMS only: the picklist being added to. The allocation dialog asks which serials OTHER open picklists
   * hold, and this picklist's own are not another's (they are deducted through `existingPicklistItems`).
   */
  targetPicklistId?: number;
  /** Called with the validated items payload. Throw to show error. */
  onSubmit: (items: CreatePicklistItemRequest[]) => Promise<void>;
}


/**
 * ONE row of the creation table, memoised.
 *
 * ⚡ Selecting an item changes state on the panel, so the panel re-renders — and without this
 * boundary every row re-rendered with it, all 39 of them and their 148 tooltip roots, for a tick
 * that changes one checkbox. `row` and `startsGroup` are stable across a selection change (the data
 * and table state are untouched), so memo bails out and the row is left alone.
 *
 * 🪤 86d39bx22 — a memoised row that reads `row.getVisibleCells()` serves STALE cells when column
 * VISIBILITY changes, because the compiler cannot see that table state. This table has ten fixed
 * columns and never toggles them, which is the documented safe case. The tick-and-assert checks in
 * `e2e/15-picklist-cart-by-item.spec.ts` are the guard if that ever stops being true.
 *
 * The checkbox still updates because it reads `PicklistSelectionProvider`: a context change reaches
 * its consumers THROUGH a memo boundary, which is the whole reason selection moved to context.
 */
const PicklistTableRow = memo(function PicklistTableRow({
  row,
  startsGroup,
  isItem,
}: {
  row: Row<PicklistCreationTableRow>;
  startsGroup: boolean;
  isItem: boolean;
}) {
  return (
                        <Fragment key={row.id}>
                          {startsGroup && <ItemGroupSubHeader />}
                          <TableRow
                            className={
                              isItem
                                ? 'bg-muted/50 hover:bg-muted/70 !border-l-4 !border-l-primary/30'
                                : 'cursor-pointer bg-background font-medium'
                            }
                            onClick={() => {
                              if (row.getCanExpand()) {
                                row.toggleExpanded();
                              }
                            }}
                          >
                            {row.getVisibleCells().map((cell) => {
                              // Item rows fold code + name into ONE cell spanning identifier and
                              // description. The description cell has to be dropped, not just
                              // emptied — a colSpan without a matching removal shunts every later
                              // cell one column right, and the header stops lining up.
                              // Item rows lay out differently from MRS rows, and may: the group
                              // sub-header above them labels the item columns, so they are not
                              // bound to sit under the MRS header's.
                              //
                              // The identity cell takes four columns, and dept / requested /
                              // stock move right into the three that are EMPTY on an item row
                              // (approver, items, actions). Nothing is lost and the item name —
                              // the densest thing on this screen — roughly doubles.
                              //
                              //   col  0      1       2 3 4 5        6       7      8 9
                              //   MRS  select expand  ident  desc    dept    date   appr items…
                              //   item select ·       identity────→   dept    qty    stock──→
                              if (
                                isItem &&
                                (cell.column.id === 'description' ||
                                  cell.column.id === 'approvedBy' ||
                                  cell.column.id === 'itemCount' ||
                                  cell.column.id === 'actions')
                              ) {
                                return null;
                              }

                              const rendered = flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              );

                              // Name the column on hover, for MRS rows only.
                              //
                              // Once groups are expanded, an MRS row can sit a long way below the
                              // header — with other groups' item sub-headers in between — and by
                              // then "Sep 15, 2026" no longer announces itself as Date Needed.
                              // Only columns the header actually labels qualify: the blank ones
                              // (select, expand, dept, stock, actions) have nothing to say, and
                              // item rows are already labelled by their own group sub-header.
                              const header = cell.column.columnDef.header;
                              const columnLabel = isItem
                                ? ITEM_COLUMNS_WITH_LABEL_TOOLTIP.has(cell.column.id)
                                  ? ITEM_COLUMN_LABELS[cell.column.id]
                                  : null
                                : typeof header === 'string' && header.length > 0
                                  ? header
                                  : null;

                              return (
                                <TableCell
                                  key={cell.id}
                                  colSpan={
                                    isItem
                                      ? cell.column.id === 'identifier'
                                        ? 4
                                        : cell.column.id === 'stockStatus'
                                          ? 2
                                          : undefined
                                      : undefined
                                  }
                                  className={isItem ? 'py-2 text-sm' : ''}
                                >
                                  {columnLabel ? (
                                    <Tooltip>
                                      {/*
                                        `w-fit` keeps the trigger hugging the content, so the
                                        tooltip answers a hover over the VALUE rather than over
                                        the empty half of a wide cell.
                                      */}
                                      <TooltipTrigger asChild>
                                        <div className="w-fit max-w-full">{rendered}</div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <p className="text-xs">{columnLabel}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    rendered
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </Fragment>
  );
});

export function ItemSelectionPanel({
  title,
  backPath,
  picklistNumber,
  picklistPath,
  submitLabel,
  submittingLabel,
  confirmTitle,
  confirmDescription,
  confirmButtonLabel,
  existingPicklistItems,
  targetPicklistId,
  onSubmit,
}: ItemSelectionPanelProps) {
  const [itemSelection, setItemSelection] = useState<ItemSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>(true); // All rows expanded by default
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [quantityOverrides, setQuantityOverrides] = useState<Record<string, number>>({});
  const [cartSearchQuery, setCartSearchQuery] = useState('');
  // `issues` became `short` when the cart regrouped by item: short is the only per-item warning left,
  // and it is a stock condition rather than a catch-all (14ykpqtnxbv §1).
  const [cartStatusFilter, setCartStatusFilter] = useState<'all' | 'short' | 'out_of_stock'>('all');
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isResyncing, setIsResyncing] = useState(false);
  // Keyed per PLAN (`planKey`: the item AND its unit, O-22), because the allocation dialog opens for the
  // whole item across every MRS line that asked for it. Holds the quantities chosen per (bin, lot/serial);
  // which MRS line each unit serves is chosen at pick time, so no arrangement is kept.
  const [binOverrides, setBinOverrides] = useState<Record<string, SavedAllocation>>({});
  /**
   * The cart docks beside the table when the workspace can still show a readable table column next
   * to it, and floats over the table when it cannot. Geometry is CSS (`.cart-cq-panel`); this only
   * decides whether the cart opens by itself.
   *
   * §5: the column is there from the moment the page opens, empty, waiting for the first tick — its
   * width is not something the table gains and loses as items are ticked.
   */
  const dockFits = useDockFits();
  const crumbYielded = useCrumbYield(isCartOpen && dockFits);
  const navigate = useNavigate();

  /**
   * Where the pinned toolbar's back control goes once the breadcrumb has scrolled away.
   *
   * On Add Items that is the PICKLIST you are adding to, not the picklist list — skipping past it
   * would drop you two levels out from where you are working (owner ruling 2026-09-18). The design
   * says "Back to Picklists" because it was drawn for the create route only.
   */
  const backTarget = picklistPath ?? backPath;
  const backLabel = picklistPath
    ? `Back to ${picklistNumber ?? 'picklist'}`
    : 'Back to Picklists';
  const workspaceHost = useWorkspaceHost();

  // Keyed by ITEM: one dialog shows the whole plan for that item across the picklist (§4).
  const [binDialogPlan, setBinDialogPlan] = useState<ItemPlan | null>(null);
  /** Whether the allocation dialog was opened FROM the review, and must return to it. */
  const [binFromConfirm, setBinFromConfirm] = useState(false);

  // Check if in development mode
  const isDev = import.meta.env.DEV;

  const {
    requests,
    isLoading,
    error,
    fetchAvailableForPicklist,
    refreshAvailableForPicklist,
  } = useMaterialRequestStore();

  // Subscribe to stock store - include summaryCache to trigger re-renders when stock data changes
  const stockCache = useStockStore((state) => state.summaryCache);
  const loadingItems = useStockStore((state) => state.loadingItems);
  const queueStockFetch = useStockStore((state) => state.queueStockFetch);
  const getStock = useStockStore((state) => state.getStock);
  const getStockStatus = useStockStore((state) => state.getStockStatus);
  const refreshStock = useStockStore((state) => state.refreshStock);
  // Serial-level bins, fetched only for the item whose bin dialog is open. Subscribing to the cache
  // and the in-flight set is what re-renders the dialog when its rows land.
  const detailCache = useStockStore((state) => state.detailCache);
  const loadingDetailItems = useStockStore((state) => state.loadingDetailItems);
  const detailErrorItems = useStockStore((state) => state.detailErrorItems);
  const fetchStockDetail = useStockStore((state) => state.fetchStockDetail);
  const refreshStockDetail = useStockStore((state) => state.refreshStockDetail);
  const getStockDetail = useStockStore((state) => state.getStockDetail);

  // Subscribe to pending quantity store
  const pendingQtyCache = usePendingQuantityStore((state) => state.cache);
  const pendingQtyLoading = usePendingQuantityStore((state) => state.loadingItems);
  const pendingQtyQueued = usePendingQuantityStore((state) => state._pendingItemIds);
  const queuePendingQuantityFetch = usePendingQuantityStore((state) => state.queueFetch);
  const refreshPendingQuantities = usePendingQuantityStore((state) => state.refreshAll);
  const getPendingQuantity = usePendingQuantityStore((state) => state.getPendingQuantity);

  useEffect(() => {
    fetchAvailableForPicklist();
  }, [fetchAvailableForPicklist]);

  /**
   * How much of each MRS line the target picklist already carries, and which units it has claimed.
   * Both empty on the create route, where there is no target picklist. See `picklistHoldings.ts`.
   */
  const alreadyOnPicklistByMrsItemId = useMemo(
    () => heldQuantityByMrsItem(existingPicklistItems ?? []),
    [existingPicklistItems]
  );
  const claimedQtyByItem = useMemo(
    () => claimedQuantityByItem(existingPicklistItems ?? []),
    [existingPicklistItems]
  );

  // Transform to expandable table format (backend already filters for available items — anything whose
  // availableQuantity has reached 0 never arrives, INCLUDING lines this picklist has fully covered).
  const tableData = useMemo(() => {
    return requests.map((request): MrsTableRow => ({
      rowType: 'mrs',
      id: String(request.id),
      mrsId: request.id,
      mrsNumber: request.mrsNumber,
      requestorFullName: request.requestorFullName,
      requestedAt: request.requestedAt,
      approvedBy: request.approvedBy,
      itemCount: request.items.length,
      subRows: request.items
        .map((item): ItemTableRow => ({
          rowType: 'item',
          id: `${request.id}-${item.id}`,
          mrsId: request.id,
          mrsNumber: request.mrsNumber,
          itemId: item.id,
          netsuiteItemId: item.netsuiteItemId,
          isNonCatalog: item.isNonCatalog,
          itemCode: item.itemCode,
          itemName: item.itemName,
          requestedQuantity: item.requestedQuantity,
          availableQuantity: item.availableQuantity,
          unitOfMeasure: item.unitOfMeasure,
          netsuiteItemClass: item.netsuiteItemClass,
          netsuiteItemSubClass: item.netsuiteItemSubClass,
          netsuiteDepartmentId: item.netsuiteDepartmentId,
          netsuiteDepartmentName: item.netsuiteDepartmentName,
          netsuiteEndUseId: item.netsuiteEndUseId,
          netsuiteEndUseName: item.netsuiteEndUseName,
          remarks: item.remarks ?? '',
          itemType: item.itemType ?? null,
          alreadyOnPicklist: alreadyOnPicklistByMrsItemId.get(item.id) ?? 0,
        })),
    })).filter((mrs) => mrs.subRows.length > 0);
  }, [requests, alreadyOnPicklistByMrsItemId]);

  // Filter table data based on search query (state is debounced at input level)
  const filteredTableData = useMemo(() => {
    const searchLower = tableSearchQuery.toLowerCase().trim();
    if (!searchLower) return tableData;

    return tableData.filter((mrs) => {
      // Check if MRS-level fields match
      const mrsMatches =
        mrs.mrsNumber.toLowerCase().includes(searchLower) ||
        mrs.requestorFullName.toLowerCase().includes(searchLower) ||
        mrs.approvedBy.toLowerCase().includes(searchLower);

      if (mrsMatches) return true;

      // Check if any item matches (including class/subclass/department/end use)
      const hasMatchingItem = mrs.subRows.some(
        (item) =>
          item.itemCode.toLowerCase().includes(searchLower) ||
          item.itemName.toLowerCase().includes(searchLower) ||
          item.netsuiteItemClass.toLowerCase().includes(searchLower) ||
          item.netsuiteItemSubClass.toLowerCase().includes(searchLower) ||
          item.netsuiteDepartmentName.toLowerCase().includes(searchLower) ||
          item.netsuiteEndUseName.toLowerCase().includes(searchLower)
      );

      return hasMatchingItem;
    });
  }, [tableData, tableSearchQuery]);

  // Clamp pagination to valid range when data changes (e.g., after items are removed)
  useEffect(() => {
    const totalPages = Math.ceil(filteredTableData.length / pagination.pageSize);
    const maxPageIndex = Math.max(0, totalPages - 1);

    if (pagination.pageIndex > maxPageIndex) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: maxPageIndex,
      }));
    }
  }, [filteredTableData.length, pagination.pageIndex, pagination.pageSize]);

  // Handle item selection changes
  const handleItemSelectionChange = useCallback(
    (mrsId: number, itemId: number | 'all', selected: boolean) => {
      // Queue stock fetch for newly selected items. Only stockable, linked items are fetched —
      // non-catalog (Service/dummy) and unlinked (netsuiteItemId 0) lines have no real stock and
      // must not be queued (queuing 0 was the original "Unknown, never loads" bug — 86d3gmemk).
      if (selected) {
        const mrs = tableData.find((r) => r.mrsId === mrsId);
        if (mrs) {
          const targets = (itemId === 'all'
            ? mrs.subRows
            : mrs.subRows.filter((item) => item.itemId === itemId)
          ).filter(isSelectableItem);
          const itemsToFetch = targets.map((item) => item.netsuiteItemId);

          if (itemsToFetch.length > 0) {
            queueStockFetch(itemsToFetch);
            queuePendingQuantityFetch(itemsToFetch);
          }
        }
      }

      setItemSelection((prev) => {
        const mrsKey = String(mrsId);
        const currentMrsSelection = prev[mrsKey] || {};

        if (itemId === 'all') {
          // Select/deselect all items for this MRS. Non-selectable (non-catalog / unlinked)
          // rows are never added to the cart, even on select-all (86d3gmemk).
          const mrs = tableData.find((r) => r.mrsId === mrsId);
          if (!mrs) return prev;

          const newMrsSelection: { [itemId: string]: boolean } = {};
          mrs.subRows.forEach((item) => {
            newMrsSelection[String(item.itemId)] = selected && isSelectableItem(item);
          });

          return {
            ...prev,
            [mrsKey]: newMrsSelection,
          };
        }

        // Select/deselect single item
        return {
          ...prev,
          [mrsKey]: {
            ...currentMrsSelection,
            [String(itemId)]: selected,
          },
        };
      });
    },
    [tableData, queueStockFetch, queuePendingQuantityFetch]
  );

  /**
   * STABLE column definitions — no dependencies at all.
   *
   * ⚡ They used to be rebuilt on every `itemSelection` change, which made TanStack re-render every
   * cell of every row on each tick: 239ms for the first tick, 321ms for the second, against a 100ms
   * budget. Selection now reaches the checkbox through `PicklistSelectionProvider` and stock reaches
   * the badge through its own store subscription, so a tick repaints a checkbox and an arriving
   * stock read repaints one badge.
   */
  const columns = useMemo(() => createExpandableColumns(), []);

  const selectionContext = useMemo(
    () => ({ itemSelection, onItemSelectionChange: handleItemSelectionChange }),
    [itemSelection, handleItemSelectionChange],
  );

  const table = useReactTable({
    data: filteredTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => (row.rowType === 'mrs' ? row.subRows : undefined),
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    getRowId: (row) => row.id,
    paginateExpandedRows: false, // Count only MRS rows for pagination, items still show expanded
    autoResetPageIndex: false, // Prevent page reset when data changes (e.g., after picklist creation)
    state: {
      expanded,
      pagination,
    },
  });

  // Get all selected items for display
  const selectedItems = useMemo((): SelectedItemDisplay[] => {
    const items: SelectedItemDisplay[] = [];

    /**
     * Walk the TABLE, not the selection map.
     *
     * `Object.entries` on an object with numeric-looking keys iterates in ascending NUMERIC order, so
     * the old walk ordered the cart by item id — an ordering the picker never sees. That is not
     * cosmetic: line order decides who gets stock when there is not enough, and the cart hands units
     * out in this order. An MRS whose second row is short on screen must not be the row that took the
     * stock (14ykpqtnxbv, found in the browser 2026-09-18).
     */
    tableData.forEach((mrs) => {
      const mrsItems = itemSelection[String(mrs.mrsId)];
      if (!mrsItems) return;

      mrs.subRows.forEach((item) => {
        if (!mrsItems[String(item.itemId)]) return;

        items.push({
          mrsId: mrs.mrsId,
          mrsNumber: mrs.mrsNumber,
          itemId: item.itemId,
          netsuiteItemId: item.netsuiteItemId,
          isNonCatalog: item.isNonCatalog,
          itemCode: item.itemCode,
          itemName: item.itemName,
          requestedQuantity: item.requestedQuantity,
          availableQuantity: item.availableQuantity,
          unitOfMeasure: item.unitOfMeasure,
          netsuiteItemClass: item.netsuiteItemClass,
          netsuiteItemSubClass: item.netsuiteItemSubClass,
          netsuiteDepartmentId: item.netsuiteDepartmentId,
          netsuiteDepartmentName: item.netsuiteDepartmentName,
          netsuiteEndUseId: item.netsuiteEndUseId,
          netsuiteEndUseName: item.netsuiteEndUseName,
          remarks: item.remarks,
          itemType: item.itemType ?? null,
        });
      });
    });

    return items;
  }, [itemSelection, tableData]);

  // Group selected items by MRS for display
  const selectedItemsByMrs = useMemo(() => {
    const grouped: { [mrsNumber: string]: SelectedItemDisplay[] } = {};

    selectedItems.forEach((item) => {
      if (!grouped[item.mrsNumber]) {
        grouped[item.mrsNumber] = [];
      }
      grouped[item.mrsNumber].push(item);
    });

    return grouped;
  }, [selectedItems]);

  /**
   * The status of one item judged on FREE stock (14ykpqtv7nd): the raw verdict, re-judged on on-hand less what other
   * open picklists hold. Reads the SUBSCRIBED caches directly, never a store getter, so the React Compiler sees every
   * input (86d3d1z40).
   */
  const freeStatusOf = useCallback(
    (netsuiteItemId: number, wanted: number) => {
      const key = String(netsuiteItemId);
      return freeStockStatus(
        getStockStatus(netsuiteItemId, wanted),
        stockCache[key]?.data.quantityAvailable,
        pendingQtyCache[key]?.data.totalCommittedQuantity,
        pendingQtyLoading.has(netsuiteItemId) || pendingQtyQueued.has(netsuiteItemId),
        wanted,
      );
    },
    [getStockStatus, stockCache, pendingQtyCache, pendingQtyLoading, pendingQtyQueued],
  );

  // Pre-compute stock statuses for all selected items (computed once, used by multiple filters)
  // This avoids calling getStockStatus repeatedly in multiple useMemo hooks
  const itemStockStatuses = useMemo(() => {
    // Depend on stockCache and loadingItems to re-run when stock data changes
    void stockCache;
    void loadingItems;

    const statuses = new Map<string, ReturnType<typeof getStockStatus>>();
    selectedItems.forEach((item) => {
      const key = `${item.mrsId}-${item.itemId}`;
      statuses.set(key, freeStatusOf(item.netsuiteItemId, item.availableQuantity));
    });
    return statuses;
  }, [selectedItems, freeStatusOf, stockCache, loadingItems]);

  // Helper to get pre-computed stock status for an item
  const getItemStockStatus = useCallback(
    (item: SelectedItemDisplay) => {
      return itemStockStatuses.get(`${item.mrsId}-${item.itemId}`) ?? 'unknown';
    },
    [itemStockStatuses]
  );

  /**
   * The cart's plan, one entry per ITEM AND UNIT (14ykpqtnxbv; O-22 `planKey`), so one cart card is one
   * Items-table row after creation. Stock is read per `netsuiteItemId` — what the stock store, the creation
   * payload and the server's own `PicklistBinPool` key on. The prototype keys by item code; display only.
   *
   * `void`-ing the caches is the established shape in this file (86d3d1z40): `getStock` and
   * `getPendingQuantity` read mutable store state the React Compiler cannot see, so the cache objects
   * are listed as real dependencies to force a recompute when stock lands.
   */
  const itemPlans = useMemo(() => {
    void stockCache;
    void pendingQtyCache;

    return buildItemPlans(
      selectedItems,
      (netsuiteItemId) => ({
        onHand: getStock(netsuiteItemId)?.quantityAvailable ?? null,
        // Units held by other unposted picklists. NetSuite still counts these as on hand — a DOP
        // allocation does not reach it until the MIS posts — so planning on raw on-hand would
        // silently drop a deduction this cart has always made.
        committed: getPendingQuantity(netsuiteItemId)?.totalCommittedQuantity ?? 0,
      }),
      quantityOverrides,
    );
  }, [selectedItems, getStock, getPendingQuantity, quantityOverrides, stockCache, pendingQtyCache]);

  /** Stock status per item — loading and error states are not knowable from the plan alone. */
  const planStatuses = useMemo(() => {
    void stockCache;
    void loadingItems;

    // Keyed per PLAN: each status is computed from that plan's own `wanted`, and two plans of one item (KG
    // and PCS, O-22) would overwrite each other under the NetSuite item id. Stock itself stays per item.
    // Judged on FREE stock, as the plan is (14ykpqtv7nd): an item whose every unit another picklist holds is Out of
    // Stock here, so the Out filter, the Remove notice and the disabled Create all apply to it.
    const statuses = new Map<string, ReturnType<typeof getStockStatus>>();
    itemPlans.forEach((plan) => {
      statuses.set(planKey(plan), freeStatusOf(plan.netsuiteItemId, plan.wanted));
    });
    return statuses;
  }, [itemPlans, freeStatusOf, stockCache, loadingItems]);

  const getPlanStatus = useCallback(
    (plan: ItemPlan) => planStatuses.get(planKey(plan)) ?? 'unknown',
    [planStatuses],
  );

  const outOfStockPlans = useMemo(
    () => itemPlans.filter((plan) => getPlanStatus(plan) === 'out_of_stock'),
    [itemPlans, getPlanStatus],
  );

  /**
   * Items the picklist cannot fully cover. Short is a STOCK condition — a quantity someone lowered by
   * hand is a decision, not a problem, and never lands here (owner ruling 2026-09-18).
   */
  const shortPlans = useMemo(
    () => itemPlans.filter((plan) => plan.shortfall > 0 && getPlanStatus(plan) !== 'out_of_stock'),
    [itemPlans, getPlanStatus],
  );

  /** What the review lists: everything that will actually be picked. */
  const reviewPlans = useMemo(
    () => itemPlans.filter((plan) => getPlanStatus(plan) !== 'out_of_stock' && plan.total > 0),
    [itemPlans, getPlanStatus],
  );

  /**
   * PT-7 E3 — the review's MRS coverage: for each MRS in the selection, how many of its lines this picklist
   * takes and which it leaves. Over the plans that will actually be sent, so an out-of-stock plan's lines
   * count as left out.
   */
  const coverage = useMemo(() => {
    const pickByLineKey = new Map<string, number>();
    for (const plan of reviewPlans) {
      for (const line of plan.lines) pickByLineKey.set(line.key, line.pick);
    }
    return creationCoverage(tableData, pickByLineKey);
  }, [reviewPlans, tableData]);

  /** Bin counts per plan (`planKey`, the keys of `binOverrides`) for the review's `Automatic` / `n bins chosen` line. */
  const chosenBinCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [key, saved] of Object.entries(binOverrides)) {
      if (saved.bins.length > 0) counts[key] = saved.bins.length;
    }
    return counts;
  }, [binOverrides]);

  const filteredItemPlans = useMemo(() => {
    const searchLower = cartSearchQuery.toLowerCase().trim();

    return itemPlans.filter((plan) => {
      if (searchLower) {
        const matchesSearch =
          plan.itemCode.toLowerCase().includes(searchLower) ||
          plan.itemName.toLowerCase().includes(searchLower) ||
          plan.mrsNumbers.some((mrs) => mrs.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      if (cartStatusFilter === 'short') {
        return plan.shortfall > 0 && getPlanStatus(plan) !== 'out_of_stock';
      }
      if (cartStatusFilter === 'out_of_stock') {
        return getPlanStatus(plan) === 'out_of_stock';
      }
      return true;
    });
  }, [itemPlans, cartSearchQuery, cartStatusFilter, getPlanStatus]);


  /** MRS lines selected. Still the unit the picklist RECORD is written in — one line per MRS line. */
  const selectedItemCount = selectedItems.length;
  const selectedMrsCount = Object.keys(selectedItemsByMrs).length;
  /** Distinct items — what the cart header and the `All (n)` filter count now (14ykpqtnxbv §1). */
  const cartItemCount = itemPlans.length;
  const filteredItemCount = filteredItemPlans.length;


  // Get max allowed quantity for an item
  const getMaxQuantity = useCallback(
    (item: SelectedItemDisplay): number => {
      const stock = getStock(item.netsuiteItemId);
      if (stock && stock.quantityAvailable >= 0) {
        return Math.min(item.availableQuantity, stock.quantityAvailable);
      }
      return item.availableQuantity;
    },
    [getStock]
  );

  // Handle quantity change in cart
  const handleQuantityChange = useCallback(
    (item: SelectedItemDisplay, newQty: number) => {
      const key = `${item.mrsId}-${item.itemId}`;
      // ⚠️ TWO GRAINS, AND THEY ARE NOT INTERCHANGEABLE. `quantityOverrides` is keyed per MRS LINE
      // (`mrsId-itemId`); `binOverrides` is keyed per PLAN (`planKey`), because the allocation dialog
      // opens for the whole item across every line. Both deletes below once used `key`, so neither
      // ever matched a binOverrides entry and the invalidation NEVER FIRED — a saved allocation
      // survived the quantity change it was supposed to be discarded by, and the payload then split
      // a stale bin set across the new plan, silently dropping bins or leaving a line short.
      // `itemId` is the MaterialRequestItem row id (see `itemId: item.id` where the rows are built),
      // NOT the NetSuite item id, so the two key shapes never even collided by accident.
      const itemBinKey = planKey(item);
      const maxAllowed = getMaxQuantity(item);
      const clampedQty = Math.max(1, Math.min(newQty, maxAllowed));

      if (clampedQty === item.availableQuantity && !quantityOverrides[key]) {
        // No change needed
        return;
      }

      setQuantityOverrides((prev) => ({
        ...prev,
        [key]: clampedQty,
      }));

      // Clear the item's saved allocation — its plan total just moved under it.
      setBinOverrides((prev) => {
        if (!(itemBinKey in prev)) return prev;
        const rest = { ...prev };
        delete rest[itemBinKey];
        return rest;
      });
    },
    [getMaxQuantity, quantityOverrides]
  );

  // Reset quantity to max available
  const handleResetQuantity = useCallback(
    (item: SelectedItemDisplay) => {
      const key = `${item.mrsId}-${item.itemId}`;
      const itemBinKey = planKey(item); // per PLAN — see handleQuantityChange
      setQuantityOverrides((prev) => {
        const rest = { ...prev };
        delete rest[key];
        return rest;
      });
      setBinOverrides((prev) => {
        if (!(itemBinKey in prev)) return prev;
        const rest = { ...prev };
        delete rest[itemBinKey];
        return rest;
      });
    },
    []
  );


  // Check if any items are still loading or have unknown stock status
  const hasItemsWithPendingStock = useMemo(() => {
    return selectedItems.some((item) => {
      const status = getItemStockStatus(item);
      return status === 'unknown' || status === 'loading';
    });
  }, [selectedItems, getItemStockStatus]);

  // Check if any items are completely out of stock (blocks creation)
  const hasOutOfStockItems = useMemo(() => {
    return selectedItems.some((item) => {
      const status = getItemStockStatus(item);
      return status === 'out_of_stock';
    });
  }, [selectedItems, getItemStockStatus]);

  /**
   * Items whose stock lookup FAILED. These block creation (owner ruling): a picklist must not be cut
   * against stock nobody could verify. `error` previously gated nothing, so an unverified item was
   * silently submittable — a default nobody chose.
   *
   * The block is only honest because it is clearable: `handleRetryStock` below re-fetches, and these
   * items are named in the cart so the user can see which ones to act on.
   */
  const stockErrorItems = useMemo(() => {
    return selectedItems.filter((item) => getItemStockStatus(item) === 'error');
  }, [selectedItems, getItemStockStatus]);

  const hasStockErrorItems = stockErrorItems.length > 0;

  const handleRetryStock = useCallback(() => {
    const ids = Array.from(new Set(stockErrorItems.map((item) => item.netsuiteItemId)));
    if (ids.length > 0) void refreshStock(ids);
  }, [stockErrorItems, refreshStock]);






  // Handle submit button click - always show confirmation dialog
  const handleSubmitClick = () => {
    setSubmitError(null);
    setShowConfirmation(true);
  };

  // Handle resync items (dev only) - updates existing items with latest NetSuite data
  const handleResyncItems = async () => {
    setIsResyncing(true);
    try {
      const result = await materialRequestService.resyncItems();
      if (result.success) {
        toast.success('Items Resynced', {
          description: `Updated ${result.newRecordsCreated} items with latest data from NetSuite`,
        });
        // Refresh the list to show updated data
        await refreshAvailableForPicklist();
      } else {
        toast.error('Resync Failed', {
          description: result.errors?.join(', ') || 'Unknown error',
        });
      }
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to resync items');
      toast.error('Resync Failed', { description: message });
    } finally {
      setIsResyncing(false);
    }
  };

  // Build items payload and delegate to onSubmit
  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const items: CreatePicklistItemRequest[] = [];

      // ONE ROW PER MRS LINE — the view consolidates, the record never does. The release ledger is per
      // MRS line and the issuance slip carries a memo per line, so two lines of one item must stay two
      // lines however the cart draws them.
      // ISS-R-028 — an item with nothing on hand never goes on a picklist: the user removes it from the cart. The
      // button is disabled while one is there; this covers stock that ran out while the review was open.
      if (outOfStockPlans.length > 0) {
        setShowConfirmation(false);
        setIsSubmitting(false);
        return;
      }

      for (const plan of itemPlans) {
        // Bins are chosen for the ITEM; the record is per MRS line, so they are split across the item's
        // lines in order — provisionally. The picker chooses the MRS line at pick time and the server
        // may move unpicked room between the lines then; no screen shows this split.
        //
        // ⚠️ LAST GATE BEFORE THE WIRE. The dialog refuses to save unless the allocation totals the
        // plan exactly, but that check runs INSIDE the dialog and the plan can move afterwards (a
        // quantity edit, a line removed). The invalidation that is supposed to discard the allocation
        // when that happens was dead for the life of this feature — it deleted the wrong key shape —
        // so a stale set really could reach here. An allocation that no longer totals the plan is
        // DROPPED rather than split: `splitBinsForPayload` would otherwise hand out whatever it had and
        // leave a line short or silently discard the surplus, and a wrong allocation is worse than none —
        // omitting it lets the server auto-allocate, which is always self-consistent.
        const saved = binOverrides[planKey(plan)];
        const savedTotal = saved?.bins.reduce((sum, b) => sum + b.allocatedQuantity, 0) ?? 0;
        const usable = saved && savedTotal === plan.total ? saved : undefined;
        const perLine = usable ? splitBinsForPayload(plan, usable.bins) : null;

        for (const line of plan.lines) {
          // A line planned at zero is DROPPED, not written as an empty line.
          if (line.pick <= 0) continue;

          const lineBins = perLine?.[line.key];
          items.push({
            materialRequestItemId: line.item.itemId,
            quantityToPick: line.pick,
            ...(lineBins && lineBins.length > 0 ? { bins: lineBins } : {}),
          });
        }
      }

      // 14ykpqtv7nd — every line planned at zero: nothing would go on the picklist. The server refuses an empty
      // picklist as a bare "Validation failed", which is what a user saw when another picklist held every unit.
      // The Out gate above should already have caught it; this says what happened if it did not.
      if (items.length === 0) {
        setSubmitError('Nothing to pick: every item in the cart is held by other picklists or out of stock.');
        setShowConfirmation(false);
        return;
      }

      await onSubmit(items);

      // 14ykpqtv7nd — the picklist just created now HOLDS these items, so what other picklists hold of them changed.
      // Re-read it, or the table's pills judge the remaining lines on a figure from before this picklist existed
      // (cached two minutes) and say In Stock about units this very picklist just took.
      void refreshPendingQuantities(itemPlans.map((plan) => plan.netsuiteItemId));

      // Clear selection and close dialog on success
      setShowConfirmation(false);
      setItemSelection({});
      setQuantityOverrides({});
      setBinOverrides({});
      setIsCartOpen(false);

      // Silent refresh - updates data without loading state (preserves table pagination/scroll)
      await refreshAvailableForPicklist();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Operation failed');
      setSubmitError(message);
      toast.error('Operation Failed', { description: message });

      // 14ykpqtv7nd — a refusal usually means stock moved under the cart: another picklist took units, or a bin
      // chosen minutes ago is now held. Re-read what decides Out and what Allocate Bins offers, so the cart and a
      // reopened dialog show the current figures rather than the ones the refusal was about.
      void refreshPendingQuantities(itemPlans.map((plan) => plan.netsuiteItemId));
      itemPlans
        .filter((plan) => binOverrides[planKey(plan)])
        .forEach((plan) => void refreshStockDetail(plan.netsuiteItemId));
    } finally {
      setIsSubmitting(false);
    }
  };

  // The plan key is a third argument rather than being derived here: `binOverrides` is keyed per PLAN
  // (`planKey`, item AND unit) and `itemId` is the MaterialRequestItem row id, so the two never match —
  // and this function cannot compute the unit. Every caller already holds the row it is removing.
  const handleRemoveItem = useCallback((mrsId: number, itemId: number, itemBinKey: string) => {
    setItemSelection((prev) => {
      const mrsKey = String(mrsId);
      const currentMrsSelection = prev[mrsKey] || {};

      return {
        ...prev,
        [mrsKey]: {
          ...currentMrsSelection,
          [String(itemId)]: false,
        },
      };
    });
    // Dropping ANY line of an item moves that item's plan total, so its saved allocation is stale
    // whether or not other lines survive. Clearing it degrades to auto-allocation, which is correct;
    // keeping it would hand `splitBinsForPayload` a bin set that no longer matches the plan.
    setBinOverrides((prev) => {
      if (!(itemBinKey in prev)) return prev;
      const rest = { ...prev };
      delete rest[itemBinKey];
      return rest;
    });
  }, []);

  /**
   * Remove one MRS line from an item (owner decision 2, 2026-09-18). With every line visible, a line
   * you can see but cannot act on is a dead end.
   */
  const handleRemoveLine = useCallback((item: SelectedItemDisplay) => {
    handleRemoveItem(item.mrsId, item.itemId, planKey(item));
  }, [handleRemoveItem]);

  /** Remove the whole item — every MRS line that composes it, and its saved allocation. */
  const handleRemoveItemPlan = useCallback((plan: ItemPlan) => {
    for (const line of plan.lines) {
      handleRemoveItem(line.item.mrsId, line.item.itemId, planKey(line.item));
    }
  }, [handleRemoveItem]);


  /**
   * The bins the allocation dialog may draw on for an item.
   *
   * This used to deduct what OTHER cart entries of the same item had already claimed, and warn about
   * it — because the dialog only ever showed one MRS line's share, so a sibling line's allocation was
   * an invisible force acting on your numbers. The dialog now opens for the whole item, so there is no
   * sibling to deduct and nothing to apologise for: the deduction, the `hasCrossEntryDeductions` flag
   * and its banner all go away with the problem they existed for (14ykpqtnxbv §4).
   *
   * It also retires a hot spot: the old version called `selectedItems.find(...)` inside a loop over
   * `binOverrides`, in render — O(overrides x lines) on every pass.
   *
   * What it DOES still deduct, on the Add Items route, is what the target picklist already holds. That is
   * not a sibling in the cart — it is a row already written to the database, and the NetSuite stock read
   * has no idea we are holding it. Offering it would let the user choose a unit `PicklistBinPool.Reserve`
   * then refuses on submit (see `claimedUnitsByItem`).
   */
  const getEffectiveBins = useCallback(
    (plan: ItemPlan) => {
      // The DETAIL read: allocation is per serial, so the summary's per-bin totals cannot serve it.
      // Loaded lazily when the dialog opens (see handleOpenBinDialog), which is why this returns an
      // empty list on first render — the dialog shows a skeleton for that, never an empty table.
      void detailCache;
      const bins = getStockDetail(plan.netsuiteItemId)?.bins ?? [];
      return deductHoldings(bins, claimedQtyByItem.get(plan.netsuiteItemId));
    },
    [getStockDetail, detailCache, claimedQtyByItem],
  );

  /**
   * Put the cart in the workspace column so `absolute` anchors to it rather than the viewport — the
   * shell rail and panels stay uncovered whatever their state. Falls back to rendering in place when
   * the host is not there (a bare route, a test harness), where `fixed` still does the right thing.
   */
  const renderInWorkspace = useCallback(
    (node: React.ReactNode) => (workspaceHost ? createPortal(node, workspaceHost) : node),
    [workspaceHost],
  );

  // §5 — when the cart docks it is part of the page, not something you summon, so it opens itself.
  // Narrow workspaces keep today's behaviour: the round button, opened on demand.
  useEffect(() => {
    if (dockFits) setIsCartOpen(true);
  }, [dockFits]);

  /**
   * Open the bin dialog immediately and start its fetch behind it — never await before the first
   * visible change. The dialog renders a skeleton until the serials arrive.
   */
  const handleOpenBinDialog = useCallback(
    (plan: ItemPlan, fromConfirm = false) => {
      // 14ykpqtv7nd — never open for a plan of nothing: every row would read "over". An item whose free stock is 0
      // is Out of Stock and has no Bins button; this is the guard behind that.
      if (plan.total <= 0) return;
      // ONE owner for this handoff. Setting `showConfirmation` from the row handler as well left both
      // dialogs mounted: two scrims stacked, and the review's confirm button still tabbable behind
      // the allocation dialog. Never let two be open at once — the one behind stays focusable.
      if (fromConfirm) {
        setShowConfirmation(false);
        setBinFromConfirm(true);
      }
      setBinDialogPlan(plan);
      void fetchStockDetail(plan.netsuiteItemId);
    },
    [fetchStockDetail],
  );

  /** The only thing that reopens the review after the allocation dialog closes, however it closed. */
  const handleBinDialogExit = useCallback(() => {
    setBinDialogPlan(null);
    if (binFromConfirm) {
      setBinFromConfirm(false);
      setShowConfirmation(true);
    }
  }, [binFromConfirm]);

  return (
    <div className={`space-y-4 pb-20 ${isCartOpen ? "cart-cq-pad" : ""}`}>
      {/*
        No Card. The card header duplicated the page title area and cost ~90px of vertical space
        above a table that is the whole point of this screen; a breadcrumb says where you are AND
        where Back goes, which the bare ArrowLeft never did. Same component the picklist detail
        page uses, so the two screens read as one place.
      */}
      {/* The top gutter lives HERE, not on the scroller: padding on the scroll container
          itself is scrollable area, so rows would ride up into it and paint above the
          pinned toolbar — the bug this screen already had once. */}
      <div className="pt-6 mb-3">
        <PicklistBreadcrumb
          backUrl={backPath}
          picklistNumber={picklistNumber}
          picklistUrl={picklistPath}
          current={title}
        />
      </div>
      <div>
        <div>
          {isLoading ? (
            <TableSkeleton rows={8} cols={6} />
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <p className="text-destructive">{error}</p>
              <button
                onClick={() => fetchAvailableForPicklist()}
                className="text-sm text-primary hover:underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Toolbar — PINNED (§6). It is used mid-scroll, so it never leaves. Opaque, or rows
                  show through it as they pass under. The breadcrumb above is ordinary page content
                  and scrolls away; the way back takes its place here. */}
              <div className="sticky top-0 z-20 flex items-center justify-between gap-4 bg-background py-2">
                {/*
                  The back control and the search are ONE left-hand cluster. Left as siblings of a
                  `justify-between` row they became two of its four slots, so the search drifted
                  inward the moment the back button appeared on scroll.

                  `display:none` until needed, never `visibility:hidden` — the latter reserved a 36px
                  gap at the toolbar's left for a control that was not there.
                */}
                <div className="flex shrink-0 items-center gap-2">
                {crumbYielded && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0"
                    onClick={() => navigate(backTarget)}
                    title={backLabel}
                    aria-label={backLabel}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                {/* Search Input - Debounced to prevent lag during typing */}
                <DebouncedSearchInput
                  value={tableSearchQuery}
                  onValueChange={setTableSearchQuery}
                  placeholder="Search MRS, requestor, or item..."
                  className="w-64"
                  inputClassName="h-9"
                />
                </div>

                <p className="text-sm text-muted-foreground">
                  Showing {table.getRowModel().rows.filter(r => r.original.rowType === 'mrs').length} of {tableData.length} pending request(s)
                  {tableSearchQuery && ` (filtered)`}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows per page:</span>
                  <Select
                    value={String(table.getState().pagination.pageSize)}
                    onValueChange={(value) => table.setPageSize(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Dev-only resync button */}
                  {isDev && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResyncItems}
                            disabled={isResyncing}
                            className="h-8"
                          >
                            {isResyncing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                            <span className="ml-1">Resync</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Update existing items with latest NetSuite data (Dev only)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>

              {/* Table */}
              {/*
                The full column set needs 1024px (the sum of COLUMN_WIDTHS). Scroll it rather than
                letting the columns squeeze — below that width Stock was the first to clip, and a
                clipped stock badge is the one cell on this screen a picker cannot afford to misread.
                At or above it there must be NO sideways scroll: if one appears, a cell's content is
                wider than its column and is spilling past the table's edge (see COLUMN_WIDTHS).
              */}
              <div className="overflow-x-auto rounded-md border">
                <PicklistSelectionProvider value={selectionContext}>
                <Table className="min-w-[1024px] table-fixed">
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          // table-fixed reads its column widths from this row, so the budget is
                          // applied here and nowhere else.
                          <TableHead
                            key={header.id}
                            style={{ width: COLUMN_WIDTHS[header.column.id] }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row, index) => {
                        const isItem = row.original.rowType === 'item';
                        // The row model is flat: an expanded MRS is immediately followed by its
                        // items. So the first item after a parent starts a group, which is where
                        // the sub-header belongs.
                        const startsGroup =
                          isItem &&
                          table.getRowModel().rows[index - 1]?.original.rowType === 'mrs';

                        return (
                          <PicklistTableRow
                            key={row.id}
                            row={row}
                            startsGroup={startsGroup}
                            isItem={isItem}
                          />
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          No pending material requests available.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                </PicklistSelectionProvider>
              </div>

              {/*
                Pagination PINS to the bottom of the scroller, for the same reason the toolbar pins
                to the top: the page is its own scroll container now, so leaving Previous / Next
                below 39 rows means scrolling the whole list to change page. Opaque + a top border,
                or rows show through it as they pass underneath.
              */}
              <div className="sticky bottom-0 z-20 flex items-center justify-between border-t border-border bg-background px-2 py-2">
                <div className="text-sm text-muted-foreground">
                  Page {table.getState().pagination.pageIndex + 1} of{' '}
                  {table.getPageCount() || 1}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cart — a docked second column or a floating panel, decided in CSS (§5). One panel, two
          geometries: never two components. */}
      {renderInWorkspace(
        isCartOpen ? (
          <div className="cart-cq-panel flex flex-col border bg-background">
            {/* Cart Header */}
            <div className="flex items-center justify-between p-3 border-b bg-muted/30">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" />
                <span className="font-medium">
                  Picklist Cart ({cartItemCount})
                </span>
                {selectedItemCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedItemCount} line{selectedItemCount === 1 ? '' : 's'} · {selectedMrsCount} MRS
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setIsCartOpen(false)}
              >
                <Minus className="h-4 w-4" />
              </Button>
            </div>

            {/* Search and Filter Bar - Sticky below header */}
            {selectedItemCount > 0 && (
              <div className="p-2 border-b bg-background space-y-2">
                {/* Search Input - Debounced to prevent lag during typing */}
                <DebouncedSearchInput
                  value={cartSearchQuery}
                  onValueChange={setCartSearchQuery}
                  placeholder="Search items..."
                  inputClassName="h-8 text-sm"
                />

                {/* Status Filter Tabs */}
                <div className="flex gap-1">
                  <Button
                    variant={cartStatusFilter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs flex-1"
                    onClick={() => setCartStatusFilter('all')}
                  >
                    All ({cartItemCount})
                  </Button>
                  <Button
                    variant={cartStatusFilter === 'short' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs flex-1"
                    onClick={() => setCartStatusFilter('short')}
                  >
                    Short ({shortPlans.length})
                  </Button>
                  <Button
                    variant={cartStatusFilter === 'out_of_stock' ? 'destructive' : 'outline'}
                    size="sm"
                    className="h-7 text-xs flex-1"
                    onClick={() => setCartStatusFilter('out_of_stock')}
                  >
                    {/* 86d3d1z40: use the reactive memoized `outOfStockItems` instead of a
                        raw inline getStockStatus() filter (which the React Compiler memoized
                        on stable deps → stale count). Mirrors the Issues count above. */}
                    Out ({outOfStockPlans.length})
                  </Button>
                </div>
              </div>
            )}

            {/* Cart Content */}
            <div className="cart-cq-scroll max-h-[24rem] overflow-y-auto">
              <div className="p-3">
                {selectedItemCount === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No items selected. Select items from the table to add them to the picklist.
                  </p>
                ) : filteredItemCount === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No items match your search or filter.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {filteredItemPlans.map((plan) => (
                      <CartItemCard
                        key={planKey(plan)}
                        plan={plan}
                        status={getPlanStatus(plan)}
                        pending={pendingQtyCache[String(plan.netsuiteItemId)]?.data ?? null}
                        binCodes={getStock(plan.netsuiteItemId)?.bins.map((b) => b.binCode) ?? []}
                        chosenBinCount={binOverrides[planKey(plan)]?.bins.length ?? null}
                        canAllocateBins={(getStock(plan.netsuiteItemId)?.bins.length ?? 0) > 0}
                        onRemoveItem={handleRemoveItemPlan}
                        onRemoveLine={handleRemoveLine}
                        onQuantityChange={handleQuantityChange}
                        onResetQuantity={handleResetQuantity}
                        onOpenBins={handleOpenBinDialog}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Cart Footer */}
            {selectedItemCount > 0 && (
              <div className="p-3 border-t bg-muted/30 space-y-3">
                {/* Loading stock indicator */}
                {hasItemsWithPendingStock && (
                  <div className="p-2 rounded-md bg-info/10 border border-info/30">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 text-info animate-spin" />
                      <p className="text-xs text-info">
                        Checking stock availability...
                      </p>
                    </div>
                  </div>
                )}

                {/*
                  ONE short summary, not two warnings.

                  This used to be a per-LINE "need X, have Y" block plus a separate "pending
                  allocations" block. Both are the same fact now: short counts stock the picklist can
                  actually take, which is on-hand MINUS what other unposted picklists hold (owner
                  ruling 2026-09-18). Two vocabularies for one condition on a 420px card was noise.

                  It stays in the footer so a shortfall is noticed WITHOUT hovering the badge.
                */}
                {!hasItemsWithPendingStock && shortPlans.length > 0 && (
                  <div className="p-2 rounded-md bg-warning/10 border border-warning/30">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                      <div className="text-xs">
                        <p className="font-medium text-warning">
                          {shortPlans.length} item{shortPlans.length === 1 ? '' : 's'} will be picked short
                        </p>
                        <ul className="mt-1 space-y-0.5 text-warning">
                          {shortPlans.slice(0, 3).map((plan) => (
                            <li key={planKey(plan)}>
                              {plan.itemCode}: short {plan.shortfall} of {plan.wanted} {plan.unitOfMeasure}
                            </li>
                          ))}
                          {shortPlans.length > 3 && (
                            <li>...and {shortPlans.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Failed stock lookups block submission, so they get a named, retryable callout —
                    a blocked button with no way to clear it is just a different dead end. */}
                {hasStockErrorItems && (
                  <div className="p-2 rounded-md bg-destructive/10 border border-destructive/30">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                      <div className="text-xs flex-1">
                        <p className="font-medium text-destructive">
                          Stock could not be checked for {stockErrorItems.length} item(s):
                        </p>
                        <ul className="mt-1 space-y-0.5 text-destructive">
                          {stockErrorItems.slice(0, 3).map((item) => (
                            <li key={`stock-error-${item.mrsId}-${item.itemId}`}>{item.itemCode}</li>
                          ))}
                          {stockErrorItems.length > 3 && (
                            <li>...and {stockErrorItems.length - 3} more</li>
                          )}
                        </ul>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs mt-2"
                          onClick={handleRetryStock}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Retry stock check
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ISS-R-028 — Create is disabled while the cart holds an item with nothing on hand (owner
                    2026-10-05). Say so, and offer the way out: the disabled button alone was a silent dead end. */}
                {outOfStockPlans.length > 0 && (
                  <div className="p-2 rounded-md bg-destructive/10 border border-destructive/30">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                      <div className="text-xs flex-1">
                        <p className="font-medium text-destructive">{outOfStockNotice(outOfStockPlans.length)}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs mt-2"
                          onClick={() => outOfStockPlans.forEach(handleRemoveItemPlan)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          {outOfStockPlans.length === 1 ? 'Remove it' : 'Remove them'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {submitError && (
                  <div className="p-2 rounded-md bg-destructive/10 border border-destructive/30">
                    <p className="text-xs text-destructive">{submitError}</p>
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={handleSubmitClick}
                  disabled={
                    hasItemsWithPendingStock || hasOutOfStockItems || hasStockErrorItems || isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {submittingLabel}
                    </>
                  ) : hasItemsWithPendingStock ? (
                    'Checking Stock...'
                  ) : hasStockErrorItems ? (
                    // Never "Checking Stock..." here — that reads as still-loading and would leave
                    // the user waiting on something that already finished and failed.
                    'Stock Check Failed'
                  ) : (
                    submitLabel
                  )}
                </Button>
              </div>
            )}
          </div>
        ) : (
          // Collapsed — the round button, in both geometries (§5).
          <div className="fixed bottom-6 right-6 z-50">
            <Button
              size="lg"
              className="h-14 w-14 rounded-full shadow-lg relative"
              onClick={() => setIsCartOpen(true)}
              title="Open picklist cart"
              aria-label="Open picklist cart"
            >
              <ShoppingCart className="h-6 w-6" />
              {cartItemCount > 0 && (
                <Badge
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center text-xs"
                  variant="destructive"
                >
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </Badge>
              )}
            </Button>
          </div>
        ),
      )}

      {/* Detail Sheet */}
      <MaterialRequestDetailSheet />

      {/* Creation review (§8) — workspace-width, and the one place the per-line rule is stated */}
      <CreationReviewDialog
        open={showConfirmation}
        onOpenChange={setShowConfirmation}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirmButtonLabel}
        plans={reviewPlans}
        coverage={coverage}
        chosenBinCounts={chosenBinCounts}
        onViewBins={(plan) => handleOpenBinDialog(plan, true)}
        onConfirm={handleConfirmSubmit}
        isSubmitting={isSubmitting}
        submitError={submitError}
      />

      {/* Allocate Bins — opens for the ITEM and shows its whole plan across the picklist (§4) */}
      {binDialogPlan && (() => {
        const key = planKey(binDialogPlan);
        const effectiveBins = getEffectiveBins(binDialogPlan);
        // 86d3d1z40 — read the DIRECTLY SUBSCRIBED values, never a store getter called in render.
        // A getter reads mutable state the React Compiler cannot track, so it memoizes on the stable
        // function reference and serves a stale answer: the dialog would sit on its skeleton after
        // the fetch had already landed.
        const isLoadingBins = loadingDetailItems.has(binDialogPlan.netsuiteItemId);
        const binsError = detailErrorItems[String(binDialogPlan.netsuiteItemId)] ?? null;
        return (
          <BinAllocationDialog
            open={!!binDialogPlan}
            onOpenChange={(open) => { if (!open) handleBinDialogExit(); }}
            plan={binDialogPlan}
            availableBins={effectiveBins}
            isLoadingBins={isLoadingBins}
            binsError={binsError}
            onRetryBins={() => void refreshStockDetail(binDialogPlan.netsuiteItemId)}
            excludePicklistId={targetPicklistId}
            currentOverride={binOverrides[key] ?? null}
            onSave={(allocations) => {
              setBinOverrides((prev) => ({ ...prev, [key]: allocations }));
            }}
            onReset={() => {
              setBinOverrides((prev) => {
                const rest = { ...prev };
                delete rest[key];
                return rest;
              });
            }}
          />
        );
      })()}
    </div>
  );
}
